/* eslint-disable react/no-danger */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Button, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone'; // Importa o moment-timezone
import 'moment/locale/pt-br';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';

// Função para converter caminhos de arquivo para URLs de download
const getPublicFilePath = (filePath, authToken, apiBaseUrl) => {
  if (!filePath) return '';

  const normalizedPath = String(filePath).replace(/\\/g, '/');
  let finalUrl = '';

  // Se já for uma URL absoluta
  if (/^https?:\/\//i.test(normalizedPath)) {
    finalUrl = normalizedPath;
  } else {
    // Converte /public/ ou /api/public/ para /download/ (sem /api/ pois apiBaseUrl já tem)
    let convertedPath = normalizedPath;
    if (convertedPath.startsWith('/api/public/')) {
      convertedPath = convertedPath.replace('/api/public/', '/download/');
    } else if (convertedPath.startsWith('/api/download/')) {
      convertedPath = convertedPath.replace('/api/download/', '/download/');
    } else if (convertedPath.startsWith('/public/')) {
      convertedPath = convertedPath.replace('/public/', '/download/');
    } else if (!convertedPath.startsWith('/')) {
      convertedPath = `/download/${convertedPath}`;
    }

    // Constrói a URL completa (apiBaseUrl já contém /api)
    finalUrl = `${apiBaseUrl}${convertedPath}`;
  }

  // Adiciona o token na query string se disponível
  if (authToken && authToken !== false && finalUrl.includes('/download/')) {
    const separator = finalUrl.includes('?') ? '&' : '?';
    finalUrl = `${finalUrl}${separator}token=${encodeURIComponent(authToken)}`;
  }

  return finalUrl;
};

// Converte todas as URLs /api/download/... no HTML para URL completa com token (para imagens carregarem)
const resolveDownloadUrlsInHtml = (html, authToken, apiBaseUrl) => {
  if (!html || typeof html !== 'string') return html;
  return html.replace(
    /(\bsrc\s*=\s*["'])(\/api\/download\/[^"']+)(["'])/gi,
    (match, prefix, path, suffix) => {
      const fullUrl = getPublicFilePath(path, authToken, apiBaseUrl);
      return `${prefix}${fullUrl}${suffix}`;
    }
  );
};

// Função para processar mensagem HTML e substituir/reparar referências cid:
const processEmailMessage = (message, files, authToken, apiBaseUrl) => {
  if (!message || typeof message !== 'string') return message;

  let processedMessage = message;

  // Remove ou substitui referências cid: que não podem ser resolvidas (fallback para pretickets antigos)
  // Padrão: <img src="cid:...">
  const cidRegex = /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*?)>/gi;

  processedMessage = processedMessage.replace(
    cidRegex,
    (match, before, cid, after) => {
      // Tenta encontrar um arquivo correspondente na lista de anexos
      if (files && Array.isArray(files) && files.length > 0) {
        const imageFiles = files.filter((f) => {
          const filePath = typeof f === 'string' ? f : f.path || f;
          return /\.(jpg|jpeg|png|gif|webp)$/i.test(filePath);
        });
        if (imageFiles.length > 0) {
          const filePath =
            typeof imageFiles[0] === 'string'
              ? imageFiles[0]
              : imageFiles[0].path || imageFiles[0];
          const imageUrl = getPublicFilePath(filePath, authToken, apiBaseUrl);
          return `<img${before}src="${imageUrl}"${after} style="max-width: 100%; height: auto;">`;
        }
      }
      return '';
    }
  );

  // Converte src="/api/download/..." para URL completa com token (imagens inline já resolvidas no backend)
  processedMessage = resolveDownloadUrlsInHtml(
    processedMessage,
    authToken,
    apiBaseUrl
  );

  return processedMessage;
};

// Define o locale como pt-br
moment.locale('pt-br');

// Função para extrair nome e e-mail do campo senderEmail
const extractNameAndEmail = (senderEmail) => {
  const emailRegex = /"(.*?)" <(.*?)>/;
  const match = senderEmail.match(emailRegex);
  if (match) {
    return {
      name: match[1], // Nome
      email: match[2], // E-mail
    };
  }
  // Retorna o email completo se o formato esperado não for encontrado
  return {
    name: senderEmail,
    email: senderEmail,
  };
};

export default function PreTickets() {
  const routeName = '/pretickets';
  const titleName = 'Pré-tickets';
  const navigate = useNavigate();
  const [apiErrors, setApiErrors] = useState({});
  const notification = useNotification();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const userData = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPreticket, setSelectedPreticket] = useState(null);
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || axios.defaults.baseURL;

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Nome',
        accessor: 'name', // Acessa o campo "name"
      },
      {
        Header: 'E-mail do Solicitante',
        accessor: 'email', // Acessa o campo "email"
      },
      {
        Header: 'Título',
        accessor: 'subject',
      },
      {
        Header: 'Criado em',
        accessor: 'createdAt',
        Cell: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm'), // Formata a data de criação
      },
      {
        Header: 'Ações',
        accessor: 'status',
        disableSortBy: true,
      },
    ],
    []
  );

  const confirmDelete = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        await axios.delete(`${routeName}/${id}`);
        notification('Pré-ticket removido com sucesso', 'success');
        const updated = data.filter((ticket) => ticket.id !== id);
        setData(updated);
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [data, notification]
  );

  const openView = useCallback((ticketObj) => {
    setSelectedPreticket(ticketObj);
    setShowViewModal(true);
  }, []);

  const tableData = useMemo(() => {
    return data.map((ticketObj) => {
      const { name, email } = extractNameAndEmail(ticketObj.senderEmail);
      return {
        ...ticketObj,
        name,
        email,
        status: (
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => openView(ticketObj)}
            >
              Ver
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() =>
                navigate('/pretickets/new', {
                  state: {
                    id: ticketObj.id,
                    email,
                    title: ticketObj.subject,
                    message: ticketObj.message,
                    files: ticketObj.files,
                  },
                })
              }
            >
              Abrir Ticket
            </Button>
            <ButtonModal
              onConfirm={() => confirmDelete(ticketObj.id)}
              message={`Confirma remoção do Pré-ticket ${ticketObj.id}?`}
              buttonProps={{ variant: 'danger' }}
              textModal={{
                title: 'Remover Pré-ticket',
                textConfirm: 'Remover',
              }}
            >
              Deletar
            </ButtonModal>
          </div>
        ),
      };
    });
  }, [data, confirmDelete, userData, navigate, openView]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(routeName);
        setData(response.data);
        setDataLoaded(true);
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [routeName]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title={titleName} />
      <section>
        {dataLoaded && (
          <Card className="card-table mb-5">
            <ErrorProcessor error={apiErrors} />
            <DataTable
              items={tableData}
              columns={tableColumns}
              defaultPageSize={5}
              defaultSort="createdAt"
            />
          </Card>
        )}
      </section>
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalhes do Pré-ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPreticket && (
            <div>
              <div className="mb-2">
                <strong>Solicitante:</strong>{' '}
                {extractNameAndEmail(selectedPreticket.senderEmail).name} (
                {extractNameAndEmail(selectedPreticket.senderEmail).email})
              </div>
              <div className="mb-2">
                <strong>Título:</strong> {selectedPreticket.subject}
              </div>
              <div className="mb-2">
                <strong>Criado em:</strong>{' '}
                {moment(selectedPreticket.createdAt).format('DD/MM/YYYY HH:mm')}
              </div>
              <div className="mb-2">
                <strong>Mensagem:</strong>
              </div>
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  minHeight: '120px',
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    let message = selectedPreticket.message || '';
                    // Processa arquivos para obter lista
                    let files = [];
                    try {
                      const raw = selectedPreticket.files;
                      files =
                        typeof raw === 'string'
                          ? JSON.parse(raw)
                          : Array.isArray(raw)
                            ? raw
                            : [];
                    } catch (_) {
                      files = [];
                    }
                    // Processa a mensagem para substituir cid: por URLs reais
                    message = processEmailMessage(
                      message,
                      files,
                      authToken,
                      API_BASE_URL
                    );
                    // Remove múltiplos <br> consecutivos
                    message = message.replace(/(<br>\s*){2,}/g, '<br><br>');
                    return message;
                  })(),
                }}
              />
              <div className="mt-3">
                <strong>Anexos:</strong>
                <ul className="mb-0">
                  {(() => {
                    let files = [];
                    const raw = selectedPreticket.files;
                    try {
                      files =
                        typeof raw === 'string'
                          ? JSON.parse(raw)
                          : Array.isArray(raw)
                            ? raw
                            : [];
                    } catch (_) {
                      files = [];
                    }
                    return files.length ? (
                      files.map((f, idx) => {
                        const filePath =
                          typeof f === 'string' ? f : f.path || f;
                        const fileName =
                          typeof f === 'string'
                            ? f.split('/').pop()
                            : f.originalname ||
                              f.name ||
                              (f.path && f.path.split('/').pop()) ||
                              'arquivo';
                        const fileUrl = getPublicFilePath(
                          filePath,
                          authToken,
                          API_BASE_URL
                        );
                        return (
                          <li key={idx}>
                            <a href={fileUrl} target="_blank" rel="noreferrer">
                              {fileName}
                            </a>
                          </li>
                        );
                      })
                    ) : (
                      <li className="text-muted">Sem anexos</li>
                    );
                  })()}
                </ul>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedPreticket && (
            <>
              <Button
                variant="success"
                onClick={() => {
                  const { email } = extractNameAndEmail(
                    selectedPreticket.senderEmail
                  );
                  navigate('/pretickets/new', {
                    state: {
                      id: selectedPreticket.id,
                      email,
                      title: selectedPreticket.subject,
                      message: selectedPreticket.message,
                      files: selectedPreticket.files,
                    },
                  });
                }}
              >
                Abrir Ticket
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowViewModal(false)}
              >
                Fechar
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
